import { Box, Button, Typography, Modal, IconButton } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, useRef, useState } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';

import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { theme } from '@store';

interface IImageUploaderProps {
  //Logo in base64 or Object URL format
  croppedImage: string;
  setCroppedImage: (logo: string) => void;
  onDelete: () => void;
}

export const ImageUploader = ({
  croppedImage,
  setCroppedImage,
  onDelete,
}: IImageUploaderProps) => {
  const [selectedImage, setSelectedImage] = useState<string>();
  const [isCropOpen, setIsCropOpen] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleFileUpload = (uploadedFile: string) => {
    setSelectedImage(uploadedFile);
    setIsCropOpen(true);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size < 3097152) {
        handleFileUpload(URL.createObjectURL(event.target.files[0]));
        event.target.value = '';
      } else {
        alert('File is too big!');
        event.target.value = '';
      }
    }
  };

  const handleSaveCrop = async () => {
    const image = imgRef.current;
    if (crop && selectedImage && image) {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');

      const pixelRatio = window.devicePixelRatio;
      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;
      if (ctx) {
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        );

        // Converting to base64
        const base64Image = canvas.toDataURL('image/webp');

        setCroppedImage(base64Image);
      }
      setIsCropOpen(false);
    }
  };

  const handleDragEnter = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileUpload(URL.createObjectURL(event.dataTransfer.files[0]));
    }
  };

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const closeDeleteModal = () => {
    setIsConfirmDeleteOpen(false);
  };

  const handleDelete = async () => {
    setCroppedImage('');
    setIsCropOpen(false);
    setIsConfirmDeleteOpen(false);
    await onDelete();
  };

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        name='myImage'
        accept='image/jpg, image/jpeg, image/png, image/svg'
        onChange={handleInputChange}
        onDrop={handleDrop}
        style={{ display: 'none' }}
      />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '1rem',
          py: '1rem',
          mb: '1rem',
          border: theme => `1px dashed ${theme.palette.grey[400]}`,

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          backgroundColor: isDragging
            ? theme.palette.metallicBlue.main
            : 'inherit',
          cursor: 'pointer',
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={e => {
          e.preventDefault();
          if (inputRef && inputRef?.current) {
            inputRef.current.click();
          }
        }}
      >
        {croppedImage ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              px: '1rem',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{ color: theme => theme.palette.semiBlack.main }}
            >
              {t('common.uploadLogo.uploaded')}
            </Typography>

            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation();
                setIsConfirmDeleteOpen(true);
              }}
            >
              <DeleteIcon fontSize='inherit' />
            </IconButton>
          </Box>
        ) : (
          <>
            <UploadFileIcon
              sx={{ fontSize: '1.5rem', color: 'primary.main' }}
            />
            <Typography variant='subtitle1'>
              {t('common.uploadLogo.description')}
            </Typography>
            <Typography
              variant='subtitle2'
              sx={{ color: theme => theme.palette.semiBlack.main }}
            >
              {t('common.uploadLogo.extensions')}
            </Typography>
          </>
        )}
      </Box>

      <ConfirmationModal
        title={t('common.uploadLogo.deleteConfirmationTitle')}
        message={t('common.uploadLogo.deleteConfirmationDescription')}
        onConfirm={handleDelete}
        onClose={closeDeleteModal}
        isOpen={isConfirmDeleteOpen}
      />

      {selectedImage && isCropOpen && (
        <Modal open={isCropOpen} onClose={() => setIsCropOpen(false)}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                gap: '1rem',
                display: 'flex',
                padding: '1rem',
                paddingX: '1.5rem',
                alignItems: 'center',
                flexDirection: 'column',
                backgroundColor: 'white',
                justifyContent: 'center',
              }}
            >
              <ReactCrop crop={crop} onChange={c => setCrop(c)} aspect={1}>
                <img
                  ref={imgRef}
                  alt='Logo to crop'
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                  src={selectedImage}
                />
              </ReactCrop>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  sx={{
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={() => setIsCropOpen(false)}
                  variant='contained'
                  color='error'
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={handleSaveCrop}
                  variant='contained'
                  color='secondary'
                >
                  Save crop
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};
